"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const MONO = '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace';
const LIME = '#ceff1a';
const INK = '#080400';

function IconAudit({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="10.5" cy="10.5" r="6.5" stroke={color} strokeWidth="2" />
            <path d="M15.5 15.5L21 21" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function IconDefine({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M4 12h10M4 18h6" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function IconImplement({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M9 3v3M15 3v3M4 8h16M6 8v11a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function IconConnect({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="6" cy="6" r="2.5" stroke={color} strokeWidth="2" />
            <circle cx="18" cy="18" r="2.5" stroke={color} strokeWidth="2" />
            <circle cx="18" cy="6" r="2.5" stroke={color} strokeWidth="2" />
            <path d="M8.2 6.8L15.8 6M17 8.3l-8 8" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function IconAnalyse({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 20V13M11 20V8M18 20v-6" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
        </svg>
    );
}
function IconAct({ color }: { color: string }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 17l5-5 4 3 7-8" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 6h5v5" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const steps = [
    { num: '01', title: 'Audit', desc: 'Understand your current tracking setup, platforms, data and reporting gaps.', Icon: IconAudit },
    { num: '02', title: 'Define', desc: 'Establish the events, conversions and business metrics that actually matter.', Icon: IconDefine },
    { num: '03', title: 'Implement', desc: 'Configure the required tracking across analytics, advertising platforms and the website.', Icon: IconImplement },
    { num: '04', title: 'Connect', desc: 'Bring relevant data sources together to create a clearer view of the customer journey.', Icon: IconConnect },
    { num: '05', title: 'Analyse', desc: 'Identify patterns, inefficiencies and opportunities across campaigns and channels.', Icon: IconAnalyse },
    { num: '06', title: 'Act', desc: 'Turn the findings into decisions around budget, campaigns, audiences and customer journeys.', Icon: IconAct },
];

function FlowLine({ orientation }: { orientation: 'horizontal' | 'vertical' }) {
    const isH = orientation === 'horizontal';
    return (
        <div
            className="absolute pointer-events-none"
            style={
                isH
                    ? { left: 0, right: 0, top: 27, height: 2 }
                    : { top: 0, bottom: 0, left: 27, width: 2 }
            }
        >
            <div className="absolute inset-0" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <motion.div
                className="absolute inset-0"
                style={{
                    backgroundImage: isH
                        ? `repeating-linear-gradient(90deg, ${LIME} 0px, ${LIME} 6px, transparent 6px, transparent 16px)`
                        : `repeating-linear-gradient(180deg, ${LIME} 0px, ${LIME} 6px, transparent 6px, transparent 16px)`,
                    opacity: 0.85,
                }}
                animate={isH ? { backgroundPositionX: [0, 32] } : { backgroundPositionY: [0, 32] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
            />
        </div>
    );
}

export default function AnalyticsProcess() {
    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1300px] mx-auto relative z-10 flex flex-col gap-14">
                <div className="flex flex-col gap-4 max-w-[700px]">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase flex items-center gap-2"
                        style={{ color: LIME, fontFamily: CLASH }}
                    >
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: LIME }} />
                        How We Work
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: '#ffffff' }}
                    >
                        Our <span style={{ color: LIME }}>Measurement Pipeline</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="text-sm md:text-base leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.45)' }}
                    >
                        Data flows through six stages before it becomes a decision.
                    </motion.p>
                </div>

                {/* Desktop: horizontal pipeline */}
                <div className="hidden lg:block relative">
                    <FlowLine orientation="horizontal" />
                    <div className="grid relative z-10" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
                        {steps.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.08, ease: 'easeOut' }}
                                className="flex flex-col gap-4 pr-4"
                            >
                                <div
                                    className="w-[54px] h-[54px] rounded-2xl flex items-center justify-center shrink-0"
                                    style={{ background: '#0c0a08', border: '1.5px solid rgba(206,255,26,0.4)', boxShadow: '0 0 0 4px #080400' }}
                                >
                                    <s.Icon color={LIME} />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[10px]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.3)' }}>
                                        step_{s.num}
                                    </span>
                                    <h3 className="text-base font-bold" style={{ fontFamily: CLASH, color: '#ffffff' }}>
                                        {s.title}
                                    </h3>
                                    <p className="text-xs leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.45)' }}>
                                        {s.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Mobile / tablet: vertical pipeline */}
                <div className="flex lg:hidden relative pl-1">
                    <FlowLine orientation="vertical" />
                    <div className="flex flex-col gap-8 relative z-10 w-full">
                        {steps.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.08, ease: 'easeOut' }}
                                className="flex items-start gap-4"
                            >
                                <div
                                    className="w-[54px] h-[54px] rounded-2xl flex items-center justify-center shrink-0"
                                    style={{ background: '#0c0a08', border: '1.5px solid rgba(206,255,26,0.4)', boxShadow: '0 0 0 4px #080400' }}
                                >
                                    <s.Icon color={LIME} />
                                </div>
                                <div className="flex flex-col gap-1.5 pt-1">
                                    <span className="text-[10px]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.3)' }}>
                                        step_{s.num}
                                    </span>
                                    <h3 className="text-base font-bold" style={{ fontFamily: CLASH, color: '#ffffff' }}>
                                        {s.title}
                                    </h3>
                                    <p className="text-xs leading-relaxed max-w-[420px]" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.45)' }}>
                                        {s.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
